import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Plus,
  Users,
  Eye,
  Loader2,
  GraduationCap,
  BarChart3,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ClassesPage = () => {
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [newClass, setNewClass] = useState({
    name: '',
    grade_level: 1,
    academic_year: '2025-2026',
    teacher_id: '',
  });

  const fetchClasses = async () => {
    try {
      const response = await fetch(`${API_URL}/api/classes`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setClasses(data);
      }
    } catch (error) {
      console.error('Failed to fetch classes:', error);
      toast.error('Sınıflar yüklenirken hata oluştu');
    }
  };

  const fetchTeachers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users?role=teacher`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setTeachers(data);
      }
    } catch (error) {
      console.error('Failed to fetch teachers:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchClasses(), fetchTeachers()]);
      setLoading(false);
    };
    loadData();
  }, []);

  const handleAddClass = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/classes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...newClass,
          teacher_id: newClass.teacher_id || null,
        }),
      });
      
      if (response.ok) {
        toast.success('Sınıf oluşturuldu');
        setIsAddDialogOpen(false);
        setNewClass({
          name: '',
          grade_level: 1,
          academic_year: '2025-2026',
          teacher_id: '',
        });
        fetchClasses();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Sınıf oluşturulamadı');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    } finally {
      setAddLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="classes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-primary-900">
            Sınıflar
          </h1>
          <p className="text-muted-foreground">
            {classes.length} sınıf kayıtlı
          </p>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-class-btn">
              <Plus className="mr-2 h-4 w-4" />
              Sınıf Ekle
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Yeni Sınıf Oluştur</DialogTitle>
              <DialogDescription>
                Yeni sınıf bilgilerini girin
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddClass}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Sınıf Adı</Label>
                  <Input
                    id="name"
                    placeholder="örn: 1-A Sınıfı"
                    value={newClass.name}
                    onChange={(e) => setNewClass({...newClass, name: e.target.value})}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="grade_level">Sınıf Düzeyi</Label>
                    <Select
                      value={String(newClass.grade_level)}
                      onValueChange={(value) => setNewClass({...newClass, grade_level: parseInt(value)})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5, 6, 7, 8].map(level => (
                          <SelectItem key={level} value={String(level)}>
                            {level}. Sınıf
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="academic_year">Öğretim Yılı</Label>
                    <Select
                      value={newClass.academic_year}
                      onValueChange={(value) => setNewClass({...newClass, academic_year: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="2024-2025">2024-2025</SelectItem>
                        <SelectItem value="2025-2026">2025-2026</SelectItem>
                        <SelectItem value="2026-2027">2026-2027</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="teacher_id">Sınıf Öğretmeni (Opsiyonel)</Label>
                  <Select
                    value={newClass.teacher_id}
                    onValueChange={(value) => setNewClass({...newClass, teacher_id: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Öğretmen seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Atanmamış</SelectItem>
                      {teachers.map(teacher => (
                        <SelectItem key={teacher.user_id} value={teacher.user_id}>
                          {teacher.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                  İptal
                </Button>
                <Button type="submit" disabled={addLoading || !newClass.name}>
                  {addLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Oluştur
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Classes Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {classes.map((cls) => (
          <Card key={cls.class_id} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-lg">{cls.name}</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    {cls.academic_year}
                  </p>
                </div>
                <Badge variant="outline" className="bg-primary/10 text-primary">
                  {cls.grade_level}. Sınıf
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Öğrenci Sayısı
                  </span>
                  <span className="font-medium">{cls.student_count}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground flex items-center gap-2">
                    <GraduationCap className="h-4 w-4" />
                    Öğretmen
                  </span>
                  <span className="font-medium">{cls.teacher_name || 'Atanmamış'}</span>
                </div>
              </div>
              
              <div className="flex gap-2 mt-4 pt-4 border-t">
                <Button variant="outline" size="sm" className="flex-1" asChild>
                  <Link to={`/classes/${cls.class_id}`}>
                    <Eye className="mr-2 h-4 w-4" />
                    Detay
                  </Link>
                </Button>
                <Button variant="outline" size="sm" className="flex-1" asChild>
                  <Link to={`/classes/${cls.class_id}/stats`}>
                    <BarChart3 className="mr-2 h-4 w-4" />
                    İstatistik
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        
        {classes.length === 0 && (
          <div className="col-span-full text-center py-12 text-muted-foreground">
            Henüz sınıf oluşturulmamış
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassesPage;
